import { callStated, ConfirmationResult, setError } from '@aston/foundation/signals';
import { IConfirmModalContent, IConfirmModalResult } from '@aston/foundation/models';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { inject, Signal } from '@angular/core';

import { IImportItem } from '../../models';
import { ImportStatus, ImportStep, ImportType } from '../../enums';
import { ImportService } from '../../services';

export function withImportDetailVarious<_>(IMPORT_ITEM, SELECTED_FILES) {
	return signalStoreFeature(
		{
			state: type<{
				importItem: IImportItem,
				selectedFiles: File[],
				synthesisModalRef: NgbModalRef,
			}>(),
			methods: type<{
				init: () => void,
				loadImport: () => void,
				setCurrentStep: (currentStep: ImportStep) => void,
				confirm: (content: IConfirmModalContent, answer: (result: IConfirmModalResult) => void) => ConfirmationResult,
			}>(),
			props: type<{ currentType: Signal<ImportType> }>(),
		},

		withMethods(store => {
			const service = inject(ImportService);

			const uploadFiles = (files: File[]) => callStated(service.uploadFiles(store.currentType(), files), store, IMPORT_ITEM)
				.then(_ => store.loadImport());

			const selectFiles = (files: File[]) => callStated(service.checkSelectedFiles(files), store, SELECTED_FILES);

			const clearFiles = () => patchState(store, {
				selectedFiles: [],
				importItem: { ...store.importItem(), currentStep: ImportStep.UploadFile, status: ImportStatus.Draft, id: null }
			});

			const selectImportType = (type: ImportType) => {
				patchState(store, { importItem: { ...store.importItem(), type } })
				return store.loadImport();
			};

			const completeImport = (id: number, updatePlatform: boolean, saveParams: boolean) =>
				callStated(service.completeImport(id, updatePlatform, saveParams), store, IMPORT_ITEM, { stateOnly: true })
					.then(_ => store.setCurrentStep(ImportStep.Imported))

			const onValidateAndUpdate = () => {
				store.confirm(
					{textsKey: `Import.Creation.ValidationAndProcessing.UpdateScenario.`},
					() => completeImport(store.importItem().id, true, true))
			}

			const confirmAbortCurrentImport = () => store.confirm(
				{ textsKey: 'Import.Confirmations.Cancel.' },
				() => abortCurrentImport()
			);

			const abortCurrentImport = () => !store.importItem()?.id
				? clearFiles()
				: callStated(service.abortImport(store.importItem().id), store, IMPORT_ITEM, { stateOnly: true })
					.then(_ => clearFiles());

			const resetError = () => setError(null, IMPORT_ITEM);

			const resetSynthesisModalRef = () => patchState(store, { synthesisModalRef: null });

			return {
				uploadFiles,
				selectFiles,
				clearFiles,
				selectImportType,
				completeImport,
				onValidateAndUpdate,
				confirmAbortCurrentImport,
				resetError,
				resetSynthesisModalRef,
			}
		})
	)
}
